// ============================================================================
// spatialHash.js — uniform grid over the bounded battlefield for all proximity
// queries (hit arcs, aggro, separation). Because the map is bounded we use a
// dense grid rather than a hash map; rebuild is O(n) each tick, queries are
// O(neighbours). No O(n^2) anywhere.
//
// Buckets are reused across ticks; a `used` list lets us clear only touched
// cells instead of the whole grid. Queries are callback-based → zero alloc.
// ============================================================================

import { MAP_HALF } from './constants.js';

export class SpatialHash {
  constructor(cellSize, worldHalf = MAP_HALF) {
    this.cell = cellSize;
    this.inv = 1 / cellSize;
    this.origin = -worldHalf - cellSize; // pad one cell so edge units stay in-range
    const span = worldHalf * 2 + cellSize * 2;
    this.gw = Math.ceil(span * this.inv) + 1;
    this.gh = this.gw;
    this.buckets = new Array(this.gw * this.gh);
    for (let i = 0; i < this.buckets.length; i++) this.buckets[i] = [];
    this.used = [];
  }

  _cx(x) {
    let c = ((x - this.origin) * this.inv) | 0;
    return c < 0 ? 0 : c >= this.gw ? this.gw - 1 : c;
  }
  _cz(z) {
    let c = ((z - this.origin) * this.inv) | 0;
    return c < 0 ? 0 : c >= this.gh ? this.gh - 1 : c;
  }

  clear() {
    const used = this.used, b = this.buckets;
    for (let i = 0; i < used.length; i++) b[used[i]].length = 0;
    used.length = 0;
  }

  /** Insert an integer id (typically a pool slot) at world (x,z). */
  insert(x, z, id) {
    const idx = this._cx(x) + this._cz(z) * this.gw;
    const bucket = this.buckets[idx];
    if (bucket.length === 0) this.used.push(idx);
    bucket.push(id);
  }

  /**
   * Invoke cb(id) for every id in cells overlapping the box [x±r, z±r].
   * The caller is responsible for the precise circular distance test.
   */
  forEachInRadius(x, z, r, cb) {
    const minCx = this._cx(x - r), maxCx = this._cx(x + r);
    const minCz = this._cz(z - r), maxCz = this._cz(z + r);
    const b = this.buckets, gw = this.gw;
    for (let cz = minCz; cz <= maxCz; cz++) {
      const row = cz * gw;
      for (let cx = minCx; cx <= maxCx; cx++) {
        const bucket = b[row + cx];
        for (let k = 0; k < bucket.length; k++) cb(bucket[k]);
      }
    }
  }

  /**
   * Same as forEachInRadius but the callback receives (id) and may return
   * `false` to stop iteration early (used by "find first" style queries).
   */
  someInRadius(x, z, r, cb) {
    const minCx = this._cx(x - r), maxCx = this._cx(x + r);
    const minCz = this._cz(z - r), maxCz = this._cz(z + r);
    const b = this.buckets, gw = this.gw;
    for (let cz = minCz; cz <= maxCz; cz++) {
      const row = cz * gw;
      for (let cx = minCx; cx <= maxCx; cx++) {
        const bucket = b[row + cx];
        for (let k = 0; k < bucket.length; k++) {
          if (cb(bucket[k]) === false) return true;
        }
      }
    }
    return false;
  }
}
